import { createContext, useContext, useEffect, useState, useRef, ReactNode, useCallback } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { getTierByProductId, getTierByPlanName, SubscriptionTier } from "@/lib/subscription-tiers";
import { identifyUser, resetAnalytics } from "@/lib/analytics";

interface SubscriptionState {
  subscribed: boolean;
  tier: SubscriptionTier | null;
  subscriptionEnd: string | null;
  status: string | null;
  trialEnd: string | null;
  weeklyCreditsUsed: number;
  weeklyCreditsLimit: number;
  creditsResetAt: string | null;
  /** True when invoice.payment_failed is inside the grace window. */
  pastDueGraceActive: boolean;
  /** True when user canceled but current_period_end is still in the future. */
  cancelAtPeriodEnd: boolean;
  loading: boolean;
}

interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  subscription: SubscriptionState;
  onboardingCompleted: boolean | null;
  signUp: (
    email: string,
    password: string,
    fullName: string,
    metadata?: Record<string, unknown>,
  ) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  refreshSubscription: () => Promise<void>;
  refreshOnboarding: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [onboardingCompleted, setOnboardingCompleted] = useState<boolean | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionState>({
    subscribed: false,
    tier: null,
    subscriptionEnd: null,
    status: null,
    trialEnd: null,
    weeklyCreditsUsed: 0,
    weeklyCreditsLimit: 0,
    creditsResetAt: null,
    pastDueGraceActive: false,
    cancelAtPeriodEnd: false,
    loading: true,
  });

  const checkOnboarding = useCallback(async () => {
    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser) return;
      const { data } = await supabase
        .from("profiles")
        .select("onboarding_completed")
        .eq("user_id", currentUser.id)
        .maybeSingle();
      setOnboardingCompleted(data?.onboarding_completed ?? false);
    } catch {
      setOnboardingCompleted(false);
    }
  }, []);

  const checkSubscription = useCallback(async () => {
    try {
      const { data, error } = await supabase.functions.invoke("check-subscription");
      if (error) throw error;

      setSubscription({
        subscribed: data.subscribed ?? false,
        // Prefer plan_name (now the primary field from check-subscription);
        // fall back to product_id for backwards compat during deploy.
        tier: getTierByPlanName(data.plan_name) ?? getTierByProductId(data.product_id),
        subscriptionEnd: data.subscription_end ?? null,
        status: data.status ?? null,
        trialEnd: data.trial_end ?? null,
        weeklyCreditsUsed: data.weekly_credits_used ?? 0,
        weeklyCreditsLimit: data.weekly_credits_limit ?? 0,
        creditsResetAt: data.credits_reset_at ?? null,
        pastDueGraceActive: data.past_due_grace_active ?? false,
        cancelAtPeriodEnd: data.cancel_at_period_end ?? false,
        loading: false,
      });
    } catch (err) {
      console.error("Error checking subscription:", err);
      setSubscription((prev) => ({ ...prev, loading: false }));
    }
  }, []);

  const hasLoadedOnceRef = useRef(false);

  useEffect(() => {
    let isActive = true;

    const applySession = (nextSession: Session | null) => {
      if (!isActive) return;

      setSession(nextSession);
      setUser(nextSession?.user ?? null);
      setLoading(false);

      if (nextSession?.user) {
        // Identify in PostHog so every event fires under the stable user id.
        identifyUser(nextSession.user.id, {
          email: nextSession.user.email,
        });
        // Only show loading state on first load, not on token refreshes
        if (!hasLoadedOnceRef.current) {
          hasLoadedOnceRef.current = true;
          setOnboardingCompleted(null);
          setSubscription((prev) => ({ ...prev, loading: true }));
        }
        void checkSubscription();
        void checkOnboarding();
        return;
      }

      hasLoadedOnceRef.current = false;
      // Clear PostHog user so the next signup is attributed correctly.
      resetAnalytics();

      setOnboardingCompleted(null);
      setSubscription({
        subscribed: false,
        tier: null,
        subscriptionEnd: null,
        status: null,
        trialEnd: null,
        weeklyCreditsUsed: 0,
        weeklyCreditsLimit: 0,
        creditsResetAt: null,
        pastDueGraceActive: false,
        cancelAtPeriodEnd: false,
        loading: false,
      });
    };

    const { data: { subscription: authSub } } = supabase.auth.onAuthStateChange(
      (event, nextSession) => {
        if (event === "INITIAL_SESSION") return;
        applySession(nextSession);
      }
    );

    void supabase.auth.getSession().then(({ data: { session: nextSession } }) => {
      applySession(nextSession);
    });

    return () => {
      isActive = false;
      authSub.unsubscribe();
    };
  }, [checkOnboarding, checkSubscription]);

  // Periodically refresh subscription status every 60 seconds
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(checkSubscription, 60000);
    return () => clearInterval(interval);
  }, [user, checkSubscription]);

  const signUp = async (
    email: string,
    password: string,
    fullName: string,
    metadata?: Record<string, unknown>,
  ) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName, ...(metadata ?? {}) },
        emailRedirectTo: `${window.location.origin}/onboarding`,
      },
    });
    if (error) throw error;
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) throw error;
  };

  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        loading,
        subscription,
        onboardingCompleted,
        signUp,
        signIn,
        signOut,
        resetPassword,
        refreshSubscription: checkSubscription,
        refreshOnboarding: checkOnboarding,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};
